class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.renderedSearchTerm = '';
    this.renderedRequest = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    // SearchForm debounces fetching. Invalidate selection immediately so Enter
    // and clicks cannot activate old content during that debounce interval.
    this.input.addEventListener('input', () => {
      if (this.searchTerm !== this.getQuery()) {
        this.clearSelection();
        this.abortController.abort();
      }
    });
    this.predictiveSearchResults.addEventListener('click', this.onResultClick.bind(this), true);
    this.predictiveSearchResults.addEventListener('auxclick', this.onResultClick.bind(this), true);
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (this.searchTerm !== newSearchTerm) {
      this.clearSelection();
      this.abortController.abort();
    }
    // Keep the displayed results, including their search action, intact until
    // the replacement arrives. Editing the old action can also change its height.
    if (!this.isOpen) this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const currentResults = this.hasCurrentResults();
    if (!currentResults) this.clearSelection();
    if (
      !this.getQuery().length ||
      (currentResults && this.querySelector('[aria-selected="true"] a'))
    ) {
      event.preventDefault();
      return;
    }
    if (!currentResults && event.submitter && this.predictiveSearchResults.contains(event.submitter)) {
      event.preventDefault();
      // Enter can target the retained term button before the primary button.
      this.submitCurrentQuery();
    }
  }

  submitCurrentQuery() {
    // Let any current submit event finish before retrying through this form.
    const form = this.input.form;
    setTimeout(() => {
      // Without a button, requestSubmit uses the form, not its first button.
      form.requestSubmit(form.querySelector('.search__button') || undefined);
    }, 0);
  }

  clearSelection() {
    this.querySelectorAll('[aria-selected="true"]').forEach((element) => element.setAttribute('aria-selected', false));
    this.input.removeAttribute('aria-activedescendant');
  }

  hasCurrentResults() {
    return this.renderedRequest === this.abortController &&
      !this.abortController.signal.aborted && this.renderedSearchTerm === this.getQuery();
  }

  onResultClick(event) {
    if (this.hasCurrentResults()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    // Implicit Enter submission clicks the default button before submitting.
    // Reroute only that keyboard action while focus remains in the query input.
    if (
      event.type === 'click' && event.detail === 0 && document.activeElement === this.input &&
      event.target.closest('button')?.type === 'submit'
    ) this.submitCurrentQuery();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true' && this.hasCurrentResults()) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (previousTerm && currentButtonText.split(previousTerm).length > 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open') || !this.hasCurrentResults()) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    if (!this.hasCurrentResults()) return;
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // Cancel superseded work, including when the next query is cached.
    this.abortController.abort();
    this.abortController = new AbortController();
    const request = this.abortController;
    this.clearSelection();
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm, request);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: request.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (request.signal.aborted || request !== this.abortController || searchTerm !== this.getQuery()) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, searchTerm, request);
      })
      .catch((error) => {
        if (request.signal.aborted || request !== this.abortController || error?.name === 'AbortError' || error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.predictiveSearchResults.setAttribute('aria-busy', 'true');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.setResultsSize();
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm, request) {
    if (request !== this.abortController || request.signal.aborted || searchTerm !== this.getQuery()) return;
    this.clearSelection();
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.renderedSearchTerm = searchTerm;
    this.renderedRequest = request;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.predictiveSearchResults.removeAttribute('aria-busy');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    this.resultsMaxHeight = Math.max(
      0, viewportTop + (viewport?.height || window.innerHeight) -
        Math.max(viewportTop, this.predictiveSearchResults.getBoundingClientRect().top)
    );
    return this.resultsMaxHeight;
  }

  setResultsSize() {
    // Reserve the complete bounded shell from the very first loading paint.
    // Matching min/max heights prevent larger OR fewer results changing it.
    const height = `${this.getResultsMaxHeight()}px`;
    this.predictiveSearchResults.style.minHeight = height;
    this.predictiveSearchResults.style.maxHeight = height;
    const viewport = window.visualViewport;
    const viewportLeft = viewport?.offsetLeft || 0;
    const viewportWidth = viewport?.width || document.documentElement.clientWidth || window.innerWidth;
    this.predictiveSearchResults.style.maxWidth = `${Math.max(
      0, viewportLeft + viewportWidth - Math.max(viewportLeft, this.predictiveSearchResults.getBoundingClientRect().left)
    )}px`;
  }

  open() {
    this.setAttribute('open', true);
    this.setResultsSize();
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.abortController.abort();
      this.input.value = '';
      this.removeAttribute('results');
    }
    this.clearSelection();
    this.removeAttribute('loading');
    this.predictiveSearchResults.removeAttribute('aria-busy');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
